import { z } from "zod";
const postgresIdentifierSchema = z
    .string()
    .trim()
    .min(1)
    .max(63)
    .regex(/^[a-z_][a-z0-9_$]*$/u, "Use a lower-case PostgreSQL identifier");
const postgresRelationSchema = z
    .string()
    .trim()
    .max(127)
    .refine((value) => {
    const parts = value.split(".");
    return parts.length === 2 && parts.every((part) => postgresIdentifierSchema.safeParse(part).success);
}, "Use a fully qualified lower-case schema.relation name");
const uniqueArray = (item) => z.array(item).transform((values) => [...new Set(values)]);
export const postgresAllowlistSchema = z
    .object({
    allowedSchemas: uniqueArray(postgresIdentifierSchema).pipe(z.array(postgresIdentifierSchema).min(1).max(100)),
    allowedRelations: uniqueArray(postgresRelationSchema).pipe(z.array(postgresRelationSchema).max(500)).default([]),
})
    .strict()
    .superRefine((value, context) => {
    const schemas = new Set(value.allowedSchemas);
    for (const relation of value.allowedRelations) {
        const schema = relation.split(".")[0];
        if (!schemas.has(schema)) {
            context.addIssue({
                code: "custom",
                path: ["allowedRelations"],
                message: `Relation schema '${schema}' must also be allowed`,
            });
        }
    }
});
export const postgresConnectionIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[A-Za-z0-9_-]+$/u);
export const postgresConnectionStatusSchema = z.enum(["pending", "healthy", "error"]);
export const postgresTransportModeSchema = z.enum([
    "verified-tls",
    "plaintext-loopback",
]);
export const postgresConnectionSkillSchema = z.object({
    skillId: z.string().trim().min(1).max(120),
    skillVersionId: z.string().trim().min(1).max(120),
    slug: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1_000),
    routingDescription: z.string().trim().max(600),
    markdown: z.string().min(1).max(100_000),
    triggerPhrases: z.array(z.string().trim().min(1).max(120)).max(64),
    version: z.number().int().positive(),
}).strict();
export const postgresConnectionSchema = postgresAllowlistSchema.extend({
    id: postgresConnectionIdSchema,
    organisationId: z.string().trim().min(1).max(200),
    displayName: z.string().trim().min(1).max(100),
    isDefault: z.boolean(),
    host: z.string().trim().min(1).max(253),
    port: z.number().int().min(1).max(65_535),
    databaseName: z.string().trim().min(1).max(128),
    usernameHint: z.string().trim().min(1).max(32),
    transportMode: postgresTransportModeSchema,
    tlsServerName: z.string().trim().min(1).max(253).optional(),
    tlsCaConfigured: z.boolean(),
    tlsClientCertConfigured: z.boolean().default(false),
    credentialRevision: z.number().int().positive(),
    status: postgresConnectionStatusSchema,
    lastVerifiedAt: z.number().int().nonnegative().optional(),
    lastErrorCode: z.string().trim().min(1).max(100).optional(),
    pluginSkill: postgresConnectionSkillSchema.optional(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
}).strict();
export const listPostgresConnectionsResponseSchema = z
    .object({ connections: z.array(postgresConnectionSchema) })
    .strict();
const postgresCredentialFieldsSchema = z
    .object({
    connectionUrl: z.string().trim().min(1).max(20_000),
    tlsCaPem: z.string().trim().min(1).max(100_000).optional(),
    tlsClientCertPem: z.string().trim().min(1).max(100_000).optional(),
    tlsClientKeyPem: z.string().trim().min(1).max(100_000).optional(),
})
    .strict();
export const createPostgresConnectionRequestSchema = postgresAllowlistSchema
    .extend({
    organisationId: z.string().trim().min(1).max(200),
    displayName: z.string().trim().min(1).max(100),
    isDefault: z.boolean().default(false),
    connectionUrl: postgresCredentialFieldsSchema.shape.connectionUrl,
    tlsCaPem: postgresCredentialFieldsSchema.shape.tlsCaPem,
    tlsClientCertPem: postgresCredentialFieldsSchema.shape.tlsClientCertPem,
    tlsClientKeyPem: postgresCredentialFieldsSchema.shape.tlsClientKeyPem,
    transportMode: postgresTransportModeSchema.default("verified-tls"),
    tlsServerName: z.string().trim().min(1).max(253).optional(),
})
    .strict();
export const createPostgresConnectionResponseSchema = postgresConnectionSchema;
export const updatePostgresConnectionRequestSchema = z
    .object({
    operation: z.literal("update"),
    displayName: z.string().trim().min(1).max(100).optional(),
    isDefault: z.boolean().optional(),
    tlsServerName: z.string().trim().min(1).max(253).nullable().optional(),
    allowedSchemas: uniqueArray(postgresIdentifierSchema)
        .pipe(z.array(postgresIdentifierSchema).min(1).max(100))
        .optional(),
    allowedRelations: uniqueArray(postgresRelationSchema)
        .pipe(z.array(postgresRelationSchema).max(500))
        .optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.displayName === undefined && value.isDefault === undefined &&
        value.tlsServerName === undefined && value.allowedSchemas === undefined &&
        value.allowedRelations === undefined) {
        context.addIssue({ code: "custom", message: "At least one field must change" });
    }
});
export const rotatePostgresConnectionRequestSchema = postgresCredentialFieldsSchema
    .extend({ operation: z.literal("rotate") })
    .strict();
export const upsertPostgresConnectionSkillRequestSchema = z.object({
    operation: z.literal("upsert-skill"),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1_000),
    routingDescription: z.string().trim().min(1).max(600),
    markdown: z.string().min(1).max(100_000),
    triggerPhrases: z.array(z.string().trim().min(1).max(120)).max(64).default([]),
}).strict();
export const mutatePostgresConnectionRequestSchema = z.discriminatedUnion("operation", [
    updatePostgresConnectionRequestSchema,
    rotatePostgresConnectionRequestSchema,
    upsertPostgresConnectionSkillRequestSchema,
]);
export const updatePostgresConnectionResponseSchema = postgresConnectionSchema;
export const rotatePostgresConnectionResponseSchema = postgresConnectionSchema;
export const deletePostgresConnectionResponseSchema = z
    .object({ ok: z.literal(true), connectionId: postgresConnectionIdSchema })
    .strict();
export const verifyPostgresConnectionRequestSchema = z.object({
    organisationId: z.string().trim().min(1).max(200),
    connectionId: postgresConnectionIdSchema,
}).strict();
export const verifyPostgresConnectionResponseSchema = postgresConnectionSchema;
/*
 * Read-only catalogue views of a verified connection for the Knowledge page:
 * the relations the policy exposes, one relation's columns, and a bounded
 * row sample. Served by the control plane, which relays to the runtime broker.
 */
export const postgresRelationSummarySchema = z
    .object({
    schema: z.string().trim().min(1),
    relation: z.string().trim().min(1),
    type: z.enum(["table", "view", "materialized_view", "foreign_table"]),
})
    .strict();
export const postgresColumnSummarySchema = z
    .object({
    name: z.string().min(1),
    dataType: z.string().min(1),
    nullable: z.boolean(),
})
    .strict();
export const listPostgresRelationsResponseSchema = z
    .object({
    connectionId: postgresConnectionIdSchema,
    relations: z.array(postgresRelationSummarySchema),
    truncated: z.boolean(),
})
    .strict();
export const describePostgresRelationResponseSchema = z
    .object({
    connectionId: postgresConnectionIdSchema,
    schema: z.string().trim().min(1),
    relation: z.string().trim().min(1),
    columns: z.array(postgresColumnSummarySchema),
    truncated: z.boolean(),
})
    .strict();
export const samplePostgresRelationResponseSchema = z
    .object({
    connectionId: postgresConnectionIdSchema,
    schema: z.string().trim().min(1),
    relation: z.string().trim().min(1),
    columns: z.array(z.string()),
    rows: z.array(z.record(z.string(), z.unknown())),
    rowCount: z.number().int().nonnegative(),
    truncated: z.boolean(),
})
    .strict();
